import ReactGA from 'react-ga4';

// Add Window interface augmentation for dataLayer
declare global {
  interface Window {
    dataLayer?: any[];
  }
}

// Replace with your actual Google Analytics measurement ID
const TRACKING_ID = 'G-KW3CCJXVJV';

// Constants for common event categories
export const EVENT_CATEGORIES = {
  BLOG: 'Blog',
  NAVIGATION: 'Navigation',
  FORM: 'ContactForm',
  USER: 'UserPreference',
  PERFORMANCE: 'Performance',
  ERROR: 'Error',
  TESTIMONIAL: 'Testimonials',
  SECTION: 'Section'
} as const;

// Constants for common event actions
export const EVENT_ACTIONS = {
  VIEW: 'View',
  CLICK: 'Click',
  SUBMIT: 'Submit',
  ERROR: 'Error',
  COMPLETE: 'Complete',
  TOGGLE: 'Toggle',
  SCROLL: 'Scroll',
  SHARE: 'Share',
  LOAD: 'Load'
} as const;

// Constants for contact form events
export const CONTACT_EVENTS = {
  FORM_START: 'FormStart',
  FIELD_FOCUS: 'FieldFocus',
  FIELD_BLUR: 'FieldBlur',
  FIELD_CHANGE: 'FieldChange',
  VALIDATION_ERROR: 'ValidationError',
  SUBMIT_ATTEMPT: 'SubmitAttempt',
  SUBMIT_SUCCESS: 'SubmitSuccess',
  SUBMIT_ERROR: 'SubmitError',
  FORM_ABANDON: 'FormAbandon',
  CONTACT_METHOD_CLICK: 'ContactMethodClick'
} as const;

// Constants for contact methods
export const CONTACT_METHODS = {
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  PHONE: 'Phone'
} as const;

/**
 * Initialize Google Analytics with enhanced configuration
 */
export const initGA = () => {
  if (typeof window !== 'undefined') {
    // Check if Google Tag Manager has already initialized GA
    if (window.dataLayer) {
      console.log('Google Tag Manager detected, skipping direct GA initialization');
      return;
    }
    
    // Direct initialization if GTM is not present
    ReactGA.initialize(TRACKING_ID, {
      gaOptions: {
        siteSpeedSampleRate: 100,
        send_page_view: false // We'll handle this manually
      }
    });
  }
};

/**
 * Track page views with enhanced data
 */
export const trackPageView = (path: string, title?: string) => {
  ReactGA.send({ 
    hitType: "pageview", 
    page: path,
    title: title,
    location: window.location.href,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    user_agent: navigator.userAgent
  });
};

/**
 * Track user events with enhanced parameters
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number,
  additionalParams?: Record<string, any>
) => {
  const eventParams = {
    category,
    action,
    label,
    value,
    page_location: window.location.href,
    page_title: document.title,
    timestamp: new Date().toISOString(),
    ...additionalParams
  };

  ReactGA.event(eventParams);
};

/**
 * Track user engagement with content
 */
export const trackEngagement = (
  contentId: string,
  contentType: string,
  engagementType: string,
  duration?: number
) => {
  trackEvent(
    'Engagement',
    engagementType,
    `${contentType}: ${contentId}`,
    duration
  );
};

/**
 * Track form interactions with detailed data
 */
export const trackFormInteraction = (
  formId: string,
  action: string,
  fieldName?: string,
  value?: string,
  isValid?: boolean
) => {
  trackEvent(
    EVENT_CATEGORIES.FORM,
    action,
    `${formId}${fieldName ? ` - ${fieldName}` : ''}`,
    undefined,
    {
      form_id: formId,
      field_name: fieldName,
      field_value: value,
      is_valid: isValid
    }
  );
};

/**
 * Track errors with detailed information
 */
export const trackError = (
  errorType: string,
  errorMessage: string,
  errorContext?: Record<string, any>
) => {
  trackEvent(
    EVENT_CATEGORIES.ERROR,
    errorType,
    errorMessage,
    undefined,
    {
      error_context: errorContext,
      stack_trace: new Error().stack
    }
  );
};

/**
 * Track user preferences and settings
 */
export const trackUserPreference = (
  preferenceType: string,
  value: string,
  previousValue?: string
) => {
  trackEvent(
    EVENT_CATEGORIES.USER,
    'PreferenceChange',
    `${preferenceType}: ${value}`,
    undefined,
    {
      previous_value: previousValue
    }
  );
};

/**
 * Track scroll depth and content visibility
 */
export const trackScrollDepth = (depth: number) => {
  trackEvent(
    EVENT_CATEGORIES.PERFORMANCE,
    'ScrollDepth',
    `Depth: ${depth}%`,
    depth
  );
};

/**
 * Track resource loading performance
 */
export const trackResourceLoad = (
  resourceType: string,
  resourceName: string,
  loadTime: number
) => {
  trackEvent(
    EVENT_CATEGORIES.PERFORMANCE,
    'ResourceLoad',
    `${resourceType}: ${resourceName}`,
    loadTime
  );
};

/**
 * Track user session information
 */
export const trackSession = (sessionData: {
  startTime: Date;
  duration: number;
  pages: number;
  interactions: number;
}) => {
  trackEvent(
    'Session',
    'End',
    'Session completed',
    sessionData.duration,
    {
      start_time: sessionData.startTime.toISOString(),
      pages_viewed: sessionData.pages,
      total_interactions: sessionData.interactions
    }
  );
};

/**
 * Track contact form field interactions
 */
export const trackContactFieldInteraction = (
  fieldName: string,
  action: 'focus' | 'blur' | 'change',
  value?: string,
  isValid?: boolean
) => {
  trackEvent(
    EVENT_CATEGORIES.FORM,
    action === 'focus' ? CONTACT_EVENTS.FIELD_FOCUS :
    action === 'blur' ? CONTACT_EVENTS.FIELD_BLUR :
    CONTACT_EVENTS.FIELD_CHANGE,
    `Field: ${fieldName}`,
    undefined,
    {
      field_name: fieldName,
      field_value: value,
      is_valid: isValid
    }
  );
};

/**
 * Track contact form abandonment
 */
export const trackFormAbandonment = (
  formData: Record<string, string>,
  timeSpent: number
) => {
  trackEvent(
    EVENT_CATEGORIES.FORM,
    CONTACT_EVENTS.FORM_ABANDON,
    'Form abandoned',
    timeSpent,
    {
      fields_filled: Object.keys(formData).filter(key => formData[key]).length,
      total_fields: Object.keys(formData).length
    }
  );
};

/**
 * Track contact method clicks with enhanced data
 */
export const trackContactMethodClick = (
  method: keyof typeof CONTACT_METHODS,
  source: string,
  context?: Record<string, any>
) => {
  trackEvent(
    EVENT_CATEGORIES.FORM,
    CONTACT_EVENTS.CONTACT_METHOD_CLICK,
    `Method: ${CONTACT_METHODS[method]}`,
    undefined,
    {
      method: CONTACT_METHODS[method],
      source,
      ...context
    }
  );
};

/**
 * Track successful form submission with enhanced data
 */
export const trackFormSubmissionSuccess = (
  formData: Record<string, string>,
  timeSpent: number
) => {
  trackEvent(
    EVENT_CATEGORIES.FORM,
    CONTACT_EVENTS.SUBMIT_SUCCESS,
    'Form submitted successfully',
    timeSpent,
    {
      fields_filled: Object.keys(formData).filter(key => formData[key]).length,
      total_fields: Object.keys(formData).length,
      has_subject: !!formData.subject,
      message_length: formData.message?.length
    }
  );
};
